import { useRef, useEffect, useState, useCallback, useMemo } from "react"
import { RefreshCw, MousePointer2 } from "lucide-react"
import { trpc } from "../../../lib/trpc"

// ============================================================================
// TYPES
// ============================================================================

type EditMode = "off" | "text" | "visual"

interface AuraPreviewProps {
  html: string
  deviceWidth: string
  isStreaming?: boolean
  editMode?: EditMode
  onHtmlChange?: (newHtml: string, changeDescription?: string) => void
  pageId?: string
  /** @deprecated Use pageId instead */
  conversationId?: string
}

// ============================================================================
// CLICK-TO-EDIT INJECTION SCRIPT
// ============================================================================

const CLICK_TO_EDIT_SCRIPT = `
<script>
(function() {
  const EDITABLE_SELECTORS = 'h1, h2, h3, h4, h5, h6, p, span, a, button, label, li, td, th, figcaption, blockquote';

  let currentlyEditing = null;
  let originalContent = null;
  let hasChanges = false;
  let isInitialized = false;

  // ========================================
  // INITIALIZATION
  // ========================================

  function init() {
    if (isInitialized) return;
    isInitialized = true;

    // Add hover styles
    addStyles();

    // Make elements editable
    setupEditableElements();

    // Global click handler to detect clicks outside
    document.addEventListener('click', handleDocumentClick, true);

    // Keyboard handler
    document.addEventListener('keydown', handleKeydown);

    // Notify parent that we're ready
    notifyParent({ type: 'EDIT_READY' });
  }

  function setupEditableElements() {
    document.querySelectorAll(EDITABLE_SELECTORS).forEach(el => {
      // Skip elements with no text or only whitespace
      if (!el.textContent?.trim()) return;

      // Skip elements that contain other editable elements (parent containers)
      if (el.querySelector(EDITABLE_SELECTORS)) return;

      // Skip script, style, etc.
      if (['SCRIPT', 'STYLE', 'SVG', 'IMG', 'INPUT', 'TEXTAREA', 'SELECT', 'CODE', 'PRE'].includes(el.tagName)) return;

      // Mark as editable
      el.dataset.auraEditable = 'true';

      // Add event listeners
      el.addEventListener('click', handleClick);
      el.addEventListener('mouseenter', handleHover);
      el.addEventListener('mouseleave', handleHoverEnd);
    });
  }

  // ========================================
  // STYLES
  // ========================================

  function addStyles() {
    if (document.getElementById('aura-edit-styles')) return;

    const style = document.createElement('style');
    style.id = 'aura-edit-styles';
    style.textContent = \`
      [data-aura-editable]:hover:not([contenteditable="true"]) {
        outline: 2px dashed rgba(59, 130, 246, 0.5) !important;
        outline-offset: 2px !important;
        cursor: text !important;
        border-radius: 2px;
      }

      [data-aura-editable][contenteditable="true"] {
        outline: 2px solid rgb(59, 130, 246) !important;
        outline-offset: 2px !important;
        background: rgba(59, 130, 246, 0.05) !important;
        border-radius: 2px;
      }

      [data-aura-editable][contenteditable="true"]:focus {
        outline: 2px solid rgb(59, 130, 246) !important;
      }
    \`;
    document.head.appendChild(style);
  }

  // ========================================
  // EVENT HANDLERS
  // ========================================

  function handleHover(e) {
    if (currentlyEditing) return;
    e.target.dataset.auraHovered = 'true';
  }

  function handleHoverEnd(e) {
    delete e.target.dataset.auraHovered;
  }

  function handleClick(e) {
    const el = e.target.closest('[data-aura-editable]');
    if (!el) return;

    // If already editing this element, let normal text selection happen
    if (el === currentlyEditing) return;

    // If editing another element, save and switch
    if (currentlyEditing) {
      saveEdit();
    }

    // Start editing
    startEdit(el);
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDocumentClick(e) {
    // If clicking outside the currently editing element, save
    if (currentlyEditing && !currentlyEditing.contains(e.target)) {
      saveEdit();
    }
  }

  function handleKeydown(e) {
    if (!currentlyEditing) return;

    // Enter (without shift) - save and exit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    }

    // Escape - cancel and revert
    if (e.key === 'Escape') {
      cancelEdit();
    }

    // Cmd/Ctrl + S - save
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      saveEdit();
    }

    // Mark as changed
    hasChanges = true;
  }

  // ========================================
  // EDIT OPERATIONS
  // ========================================

  function startEdit(el) {
    currentlyEditing = el;
    originalContent = el.innerHTML;
    hasChanges = false;

    // Make editable
    el.contentEditable = 'true';
    el.focus();

    // Select all text
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);

    // Listen for input changes
    el.addEventListener('input', () => { hasChanges = true; });

    // Notify parent that editing started
    notifyParent({ type: 'EDIT_START', elementTag: el.tagName });
  }

  function saveEdit() {
    if (!currentlyEditing) return;

    const el = currentlyEditing;
    const newContent = el.innerHTML;
    const changed = newContent !== originalContent;

    // Exit edit mode
    el.contentEditable = 'false';
    currentlyEditing = null;

    // Prevent empty elements
    if (!el.textContent?.trim()) {
      el.innerHTML = originalContent;
      notifyParent({ type: 'EDIT_CANCEL', reason: 'empty' });
      originalContent = null;
      hasChanges = false;
      return;
    }

    // If content changed, notify parent
    if (changed) {
      // Remove our injected elements before sending HTML
      const cleanHtml = getCleanHtml();

      notifyParent({
        type: 'TEXT_EDIT',
        html: cleanHtml,
        changeDescription: 'Updated ' + el.tagName.toLowerCase() + ' text',
        oldContent: originalContent,
        newContent: newContent,
      });
    } else {
      notifyParent({ type: 'EDIT_CANCEL', reason: 'no_change' });
    }

    originalContent = null;
    hasChanges = false;
  }

  function cancelEdit() {
    if (!currentlyEditing) return;

    // Revert to original
    currentlyEditing.innerHTML = originalContent;
    currentlyEditing.contentEditable = 'false';
    currentlyEditing = null;
    originalContent = null;
    hasChanges = false;

    notifyParent({ type: 'EDIT_CANCEL', reason: 'user_cancel' });
  }

  function getCleanHtml() {
    // Clone the document
    const clone = document.documentElement.cloneNode(true);

    // Remove our injected elements
    const style = clone.querySelector('#aura-edit-styles');
    if (style) style.remove();

    // Remove our script (find by content pattern)
    clone.querySelectorAll('script').forEach(script => {
      if (script.textContent?.includes('aura-edit-styles') || script.textContent?.includes('EDITABLE_SELECTORS')) {
        script.remove();
      }
    });

    // Remove data attributes
    clone.querySelectorAll('[data-aura-editable]').forEach(el => {
      delete el.dataset.auraEditable;
      delete el.dataset.auraHovered;
      el.removeAttribute('contenteditable');
    });

    return '<!DOCTYPE html>\\n' + clone.outerHTML;
  }

  // ========================================
  // PARENT COMMUNICATION
  // ========================================

  function notifyParent(data) {
    window.parent.postMessage({
      source: 'aura-preview',
      ...data
    }, '*');
  }

  // Listen for messages from parent
  window.addEventListener('message', (e) => {
    if (e.data?.target !== 'aura-preview') return;

    switch (e.data.type) {
      case 'ENABLE_EDITING':
        init();
        break;
      case 'DISABLE_EDITING':
        // Remove all editing capabilities
        if (currentlyEditing) cancelEdit();
        document.querySelectorAll('[data-aura-editable]').forEach(el => {
          delete el.dataset.auraEditable;
          el.contentEditable = 'false';
          el.removeEventListener('click', handleClick);
          el.removeEventListener('mouseenter', handleHover);
          el.removeEventListener('mouseleave', handleHoverEnd);
        });
        isInitialized = false;
        break;
      case 'SAVE_CURRENT':
        if (currentlyEditing) saveEdit();
        break;
    }
  });

  // Auto-init when script loads
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(init, 100);
  } else {
    window.addEventListener('DOMContentLoaded', () => setTimeout(init, 100));
  }
})();
</script>
`

// ============================================================================
// COMPONENT
// ============================================================================

const AuraPreview = ({
  html,
  deviceWidth,
  isStreaming = false,
  editMode = "off",
  onHtmlChange,
  pageId,
  conversationId,
}: AuraPreviewProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [key, setKey] = useState(0)
  const [isEditing, setIsEditing] = useState(false)
  const prevHtmlRef = useRef("")
  const wasStreamingRef = useRef(false)
  const stableSrcDocRef = useRef("")

  // Mutation for creating text edit versions
  const createTextEditMutation = trpc.aura.createTextEditVersion.useMutation()

  // Inject editing script when in design mode
  const htmlWithEditScript =
    editMode === "text" && html ? html.replace("</body>", `${CLICK_TO_EDIT_SCRIPT}</body>`) : html

  // Stable srcDoc - only updates when not streaming to prevent iframe reload flicker
  // During streaming, we use document.write() to update content directly
  const stableSrcDoc = useMemo(() => {
    // Update stable srcDoc only when not streaming or on initial load
    if (!isStreaming || !stableSrcDocRef.current) {
      stableSrcDocRef.current = htmlWithEditScript
    }
    return stableSrcDocRef.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlWithEditScript, isStreaming, key]) // key forces update on manual refresh

  // During streaming, write directly to iframe document to avoid reload flicker
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || !html) return

    // Skip if HTML hasn't changed
    if (html === prevHtmlRef.current) return
    prevHtmlRef.current = html

    // During streaming, write directly to iframe to avoid reload flash
    if (isStreaming && iframe.contentDocument) {
      try {
        iframe.contentDocument.open()
        iframe.contentDocument.write(htmlWithEditScript)
        iframe.contentDocument.close()
        setIsLoading(false)
      } catch {
        // If direct write fails, silently ignore - we'll reload when streaming ends
      }
    }
  }, [html, htmlWithEditScript, isStreaming])

  // When streaming ends, do one final reload to ensure clean state with scripts
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && html) {
      // Streaming just ended - reload iframe once to get clean state
      stableSrcDocRef.current = htmlWithEditScript
      setKey((k) => k + 1)
    }
    wasStreamingRef.current = isStreaming
  }, [isStreaming, html, htmlWithEditScript])

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.source !== "aura-preview") return

      switch (e.data.type) {
        case "EDIT_READY":
          console.debug("[AuraPreview] Edit mode ready")
          break

        case "EDIT_START":
          setIsEditing(true)
          console.debug("[AuraPreview] Started editing:", e.data.elementTag)
          break

        case "TEXT_EDIT":
          setIsEditing(false)
          console.debug("[AuraPreview] Text edit saved:", e.data.changeDescription)

          // Notify parent of HTML change
          if (onHtmlChange) {
            onHtmlChange(e.data.html, e.data.changeDescription)
          }

          // Create a version record if we have a page
          if (pageId && e.data.html) {
            createTextEditMutation.mutate({
              pageId,
              html: e.data.html,
              changeDescription: e.data.changeDescription,
            })
          }
          break

        case "EDIT_CANCEL":
          setIsEditing(false)
          console.debug("[AuraPreview] Edit cancelled:", e.data.reason)
          break
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [onHtmlChange, pageId, createTextEditMutation])

  // Send enable/disable message when edit mode changes
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow) return

    // Wait for iframe to load
    const sendMessage = () => {
      iframe.contentWindow?.postMessage(
        {
          target: "aura-preview",
          type: editMode === "text" ? "ENABLE_EDITING" : "DISABLE_EDITING",
        },
        "*",
      )
    }

    // Send immediately and also on load
    sendMessage()
    iframe.addEventListener("load", sendMessage)

    return () => iframe.removeEventListener("load", sendMessage)
  }, [editMode, key])

  const handleLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  const handleRefresh = useCallback(() => {
    setKey((k) => k + 1)
    setIsLoading(true)
  }, [])

  return (
    <div className="flex-1 flex flex-col items-center justify-start overflow-auto p-4">
      {/* Toolbar */}
      <div className="w-full flex items-center justify-between mb-2">
        {/* Edit mode indicator */}
        {editMode === "text" && (
          <div className="flex items-center gap-2 text-xs">
            <MousePointer2 className="w-3 h-3 text-blue-400" />
            <span className="text-blue-400">{isEditing ? "Editing..." : "Click on text to edit"}</span>
          </div>
        )}
        {editMode === "off" && <div />}

        <button
          onClick={handleRefresh}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Refresh preview"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Preview Container */}
      <div
        className={`relative bg-background rounded-lg shadow-xl overflow-hidden border transition-all duration-300 ${
          editMode === "text" ? "border-blue-500/30" : "border-border"
        }`}
        style={{
          width: deviceWidth,
          maxWidth: "100%",
          height: deviceWidth === "100%" ? "calc(100vh - 180px)" : "auto",
          aspectRatio: deviceWidth === "100%" ? undefined : "16/10",
        }}
      >
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <span>Loading preview...</span>
            </div>
          </div>
        )}

        {/* Device Frame (for mobile/tablet) */}
        {deviceWidth !== "100%" && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-t">
            {deviceWidth}
          </div>
        )}

        {/* iframe - srcDoc is stable during streaming to prevent reload flicker */}
        {/* During streaming, content is updated via document.write() to avoid reload flicker */}
        <iframe
          key={key}
          ref={iframeRef}
          srcDoc={stableSrcDoc}
          className="w-full h-full"
          sandbox="allow-scripts allow-same-origin"
          onLoad={handleLoad}
          title="Preview"
          style={{
            minHeight: deviceWidth === "100%" ? "calc(100vh - 180px)" : "500px",
          }}
        />

        {/* Editing indicator overlay */}
        {isEditing && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-md shadow-lg z-20">
            Editing • Press Enter to save, Esc to cancel
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="w-full flex items-center justify-between mt-2 px-2 text-xs text-muted-foreground">
        <span>{html.length.toLocaleString()} characters</span>
        <span className="flex items-center gap-1">
          <span className={`w-2 h-2 rounded-full ${editMode === "text" ? "bg-blue-500" : "bg-green-500"}`} />
          {editMode === "text" ? "Design Mode" : "Live Preview"}
        </span>
      </div>
    </div>
  )
}

export default AuraPreview
